/* FlowRoll free shipping progress bar
   Listens to Dawn's cartUpdate pub/sub event (add to cart, quick add,
   quantity changes, removals) and re-reads /cart.js so every bar on the
   page shows the same post-discount total. */
(() => {
  if (customElements.get('free-shipping-bar')) return;

  const store = (window.FlowRollFSB = window.FlowRollFSB || { last: {}, request: null });

  const cartUrl = () =>
    ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js';

  // One shared request when several bars update at once
  function fetchCart() {
    if (!store.request) {
      store.request = fetch(cartUrl(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then((res) => {
          if (!res.ok) throw new Error(`Cart request failed: ${res.status}`);
          return res.json();
        })
        .finally(() => {
          store.request = null;
        });
    }
    return store.request;
  }

  // Formats pence using the store's money format, dropping .00 on whole amounts
  function formatMoney(cents, format) {
    const fmt = format || '£{{amount}}';
    const whole = cents % 100 === 0;
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
    };

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      const [decimals, thousands, decimalMark] = formats[key] || formats.amount;
      const [int, frac] = (cents / 100).toFixed(whole ? 0 : decimals).split('.');
      const intWithSeparators = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? intWithSeparators + decimalMark + frac : intWithSeparators;
    });
  }

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.messageEl = this.querySelector('.fsb__message');
      this.trackEl = this.querySelector('.fsb__track');
      this.fillEl = this.querySelector('.fsb__fill');
      this.threshold = Number(this.dataset.threshold) || 0;
      this.key = this.dataset.fsbId || 'default';

      // Start from where this bar last was (0 on first load) so the fill animates
      const start = store.last[this.key];
      this.fillEl.style.transition = 'none';
      this.fillEl.style.width = `${start == null ? 0 : start}%`;
      void this.fillEl.offsetWidth;
      this.fillEl.style.transition = '';
      requestAnimationFrame(() => this.render(Number(this.dataset.total) || 0));

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }

      // Back/forward cache: page restored with a stale cart
      this.onPageShow = (event) => {
        if (event.persisted) this.refresh();
      };
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      window.removeEventListener('pageshow', this.onPageShow);
    }

    async refresh() {
      try {
        const cart = await fetchCart();
        this.render(cart.total_price);
      } catch (error) {
        console.warn('[free-shipping-bar]', error);
      }
    }

    render(total) {
      const threshold = this.threshold;
      const remaining = Math.max(threshold - total, 0);
      const percent = threshold > 0 ? Math.min((total / threshold) * 100, 100) : 100;
      const state = total <= 0 ? 'empty' : remaining > 0 ? 'progress' : 'success';

      const templates = {
        empty: this.dataset.msgEmpty,
        progress: this.dataset.msgProgress,
        success: this.dataset.msgSuccess,
      };
      const moneyFormat = this.dataset.moneyFormat;
      const message = (templates[state] || '')
        .replace(/\[amount\]/g, formatMoney(remaining, moneyFormat))
        .replace(/\[threshold\]/g, formatMoney(threshold, moneyFormat));

      if (this.messageEl.textContent !== message) this.messageEl.textContent = message;
      this.fillEl.style.width = `${percent}%`;
      this.dataset.state = state;
      this.trackEl.setAttribute('aria-valuenow', Math.round(percent));
      this.trackEl.setAttribute('aria-valuetext', message);

      store.last[this.key] = percent;
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
})();